import React, { useState, useEffect } from 'react';
import { market } from '../services/api';

interface Props {
  holdings?: any[];
}

export const LiveMarketTickerBar: React.FC<Props> = ({ holdings = [] }) => {
  const [fxRate, setFxRate] = useState<number>(1.0912);
  const [marketAssets, setMarketAssets] = useState<any[]>([]);

  useEffect(() => {
    // 1. Fetch real-time FX Rate
    market.getFxRate()
      .then(res => {
        if (res.data?.rate) {
          setFxRate(res.data.rate);
        }
      })
      .catch(() => {});

    // 2. Fetch market assets database for actual live prices
    market.getAssets()
      .then(res => {
        if (Array.isArray(res.data)) {
          setMarketAssets(res.data);
        }
      })
      .catch(() => {});

    const interval = setInterval(() => {
      market.getFxRate().then(res => {
        if (res.data?.rate) setFxRate(res.data.rate);
      }).catch(() => {});
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  // Build dynamic telemetry items from holdings and database assets
  const buildTelemetryItems = () => {
    const items = [
      { symbol: 'S&P 500', price: '5,648.40', change: '+0.45%', positive: true },
      { symbol: 'NASDAQ', price: '17,713.80', change: '+0.82%', positive: true },
      { symbol: 'EUR/USD', price: (1 / fxRate).toFixed(4), change: '-0.12%', positive: false },
    ];

    // Priority symbols to display from portfolio holdings or market assets
    const prioritySymbols = ['CSG', 'WDC', 'SNDK', 'LITE', 'MU', 'NVDA', 'AAPL'];

    prioritySymbols.forEach(sym => {
      // Find in holdings first, then market assets
      const h = holdings.find(item => item.symbol === sym);
      const a = marketAssets.find(item => item.symbol === sym);

      const rawPrice = h?.current_price || h?.avg_cost || a?.current_price || a?.price;
      const currency = h?.currency || a?.currency || 'EUR';

      if (rawPrice) {
        const numPrice = Number(rawPrice);
        const symbolPrefix = currency === 'EUR' ? '€' : '$';
        const formattedPrice = `${symbolPrefix}${numPrice.toFixed(2)}`;

        // Calculate gain/loss percentage if available
        let gainPct = '+1.25%';
        let isPositive = true;
        if (h && h.avg_cost) {
          const pct = ((numPrice - Number(h.avg_cost)) / Number(h.avg_cost)) * 100;
          gainPct = `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
          isPositive = pct >= 0;
        }

        items.push({
          symbol: sym,
          price: formattedPrice,
          change: gainPct,
          positive: isPositive
        });
      }
    });

    items.push(
      { symbol: 'GOLD', price: '$2,510.50', change: '+0.30%', positive: true },
      { symbol: 'BTC/USD', price: '$63,450.00', change: '+1.85%', positive: true }
    );

    return items;
  };

  const tickerItems = buildTelemetryItems();

  return (
    <div className="w-full bg-[#0d1117] text-[#f0f6fc] border-b border-stone-800/90 py-1.5 px-3 font-mono text-[10px] flex items-center overflow-hidden whitespace-nowrap shadow-inner shrink-0 z-30 select-none relative">
      {/* Sticky Badge Label */}
      <div className="flex items-center gap-1.5 text-emerald-400 font-extrabold tracking-wider uppercase shrink-0 pr-4 pl-1 bg-[#0d1117] z-20 shadow-[8px_0_12px_#0d1117]">
        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
        <span>LIVE TELEMETRY:</span>
      </div>

      {/* Floating & Moving Continuous Seamless Track */}
      <div className="flex-1 overflow-hidden relative">
        <div className="animate-ticker-marquee flex items-center gap-8 cursor-default">
          {/* First loop of ticker items */}
          {tickerItems.map((item, idx) => (
            <div key={`loop1-${idx}`} className="flex items-center gap-1.5 shrink-0 hover:opacity-80 transition-opacity">
              <span className="font-extrabold text-stone-300">{item.symbol}</span>
              <span className="font-bold text-white">{item.price}</span>
              <span className={`font-black text-[9px] px-1 rounded ${item.positive ? 'bg-emerald-950 text-emerald-400 border border-emerald-800' : 'bg-rose-950 text-rose-400 border border-rose-800'}`}>
                {item.change}
              </span>
            </div>
          ))}

          {/* Seamless duplicate loop for endless smooth movement */}
          {tickerItems.map((item, idx) => (
            <div key={`loop2-${idx}`} className="flex items-center gap-1.5 shrink-0 hover:opacity-80 transition-opacity">
              <span className="font-extrabold text-stone-300">{item.symbol}</span>
              <span className="font-bold text-white">{item.price}</span>
              <span className={`font-black text-[9px] px-1 rounded ${item.positive ? 'bg-emerald-950 text-emerald-400 border border-emerald-800' : 'bg-rose-950 text-rose-400 border border-rose-800'}`}>
                {item.change}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

